import { useState } from "react";
import { C, withAlpha } from "../../constants/theme";
import { MAX_PARCELAS } from "../../constants/maquininhas";
import { fmtMoeda } from "../../utils/format";
import { TIPOS_CUSTO, descreverCusto, itemDeEmbalagem } from "../../utils/vendaOnline";
import { Btn } from "../ui/Btn";

/* Peças da venda online usadas no pedido (VendaOnlineSecao) e na tela de
   padrões (Configurações → Vendas online): custos adicionais, embalagem e
   cartão. */

export const rotulo = { color:C.muted, fontSize:10, fontWeight:700, letterSpacing:1.5, display:"block", marginBottom:6 };
export const campo = { width:"100%", boxSizing:"border-box", background:C.card, border:"1px solid "+C.border, borderRadius:8, color:C.text, padding:"8px 10px", fontSize:13, outline:"none" };
export const dica = { color:C.muted, fontSize:10.5, marginTop:4, lineHeight:1.5 };
export const linkBtn = { background:"none", border:"none", padding:0, color:C.accent2, fontSize:10.5, fontWeight:700, cursor:"pointer" };

const chip = (ativo, cor = C.accent) => ({
  padding:"5px 10px", borderRadius:16, fontSize:11.5, fontWeight:700, cursor:"pointer",
  background: ativo ? withAlpha(cor, "22") : "transparent",
  border:"1px solid "+(ativo ? cor : C.border), color: ativo ? cor : C.muted,
});

/* Custos salvos viram botões liga/desliga; "+ Novo custo" cria, aplica neste
   pedido e (via onCriar) já guarda na lista pros próximos. Custo avulso que
   não está na lista (pedido antigo) aparece ligado, com ✕ pra tirar. */
export function ChipsCustos({ catalogo, ativos, onChange, onCriar }) {
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [tipo, setTipo] = useState("fixo");
  const [valor, setValor] = useState("");
  const ids = new Set(ativos.map(c => c.id));
  const avulsos = ativos.filter(c => !catalogo.some(k => k.id === c.id));
  const alternar = (c) => onChange(ids.has(c.id)
    ? ativos.filter(a => a.id !== c.id)
    : [...ativos, { id:c.id, nome:c.nome, tipo:c.tipo, valor:c.valor }]);

  const criar = async () => {
    const ok = await onCriar({ nome, tipo, valor });
    if (ok !== false) { setNome(""); setValor(""); setTipo("fixo"); setCriando(false); }
  };

  return (
    <div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
        {catalogo.map(c => (
          <button key={c.id} type="button" aria-pressed={ids.has(c.id)} onClick={() => alternar(c)} style={chip(ids.has(c.id))}>
            {ids.has(c.id) ? "✓ " : ""}{c.nome} · {descreverCusto(c)}
          </button>
        ))}
        {avulsos.map(c => (
          <button key={c.id} type="button" aria-pressed onClick={() => onChange(ativos.filter(a => a.id !== c.id))} title="Tirar deste pedido" style={chip(true)}>
            ✓ {c.nome} · {descreverCusto(c)} ✕
          </button>
        ))}
        {onCriar && !criando && (
          <button type="button" onClick={() => setCriando(true)} style={{ ...chip(false), borderStyle:"dashed", color:C.accent2 }}>+ Novo custo</button>
        )}
      </div>
      {criando && (
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center", marginTop:8 }}>
          <input aria-label="Nome do custo" autoFocus value={nome} onChange={e => setNome(e.target.value)} placeholder="Ex: Etiqueta, imposto" style={{ ...campo, flex:"1 1 140px", width:"auto" }} />
          <select aria-label="Tipo do custo" value={tipo} onChange={e => setTipo(e.target.value)} style={{ ...campo, flex:"0 1 130px", width:"auto" }}>
            {TIPOS_CUSTO.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <input aria-label="Valor do custo" type="number" min="0" step="0.01" inputMode="decimal" value={valor} onChange={e => setValor(e.target.value)}
            placeholder={tipo === "pct" ? "%" : "R$"} style={{ ...campo, flex:"0 1 80px", width:"auto" }} />
          <Btn small color={C.accent2} onClick={criar}>Salvar custo</Btn>
          <button type="button" style={linkBtn} onClick={() => setCriando(false)}>Cancelar</button>
        </div>
      )}
      {!catalogo.length && !avulsos.length && !criando && (
        <div style={dica}>Nenhum custo salvo ainda. "+ Novo custo" cria e já deixa salvo pros próximos pedidos (etiqueta, fita, imposto…).</div>
      )}
    </div>
  );
}

/* Caixas e embalagens vêm do cadastro de Materiais: custo por unidade de lá
   e baixa de estoque junto com os outros materiais do pedido. `itens` são
   entradas de materiais_usados com envio: true. */
export function EmbalagemCampos({ materiais, itens, onChange }) {
  const disponiveis = materiais.filter(m => !itens.some(i => i.id === m.id));
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      {itens.map(it => {
        const m = materiais.find(x => x.id === it.id);
        const total = (parseFloat(it.custo_unitario) || 0) * (parseFloat(it.qtd) || 0);
        return (
          <div key={it.id} style={{ display:"flex", alignItems:"center", gap:8, background:C.card, border:"1px solid "+C.border, borderRadius:8, padding:"6px 10px" }}>
            <span style={{ flex:1, minWidth:0, color:C.text, fontSize:12.5, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>📦 {it.nome}</span>
            <input aria-label={`Quantidade de ${it.nome}`} type="number" min="1" step="1" value={it.qtd}
              onChange={e => onChange(itens.map(x => x.id === it.id ? { ...x, qtd:e.target.value } : x))}
              style={{ ...campo, width:56, padding:"4px 6px", textAlign:"center" }} />
            <span style={{ color:C.muted, fontSize:11, whiteSpace:"nowrap" }}>
              {fmtMoeda(total)}{m ? ` · ${m.quantidade ?? 0} em estoque` : ""}
            </span>
            <button type="button" aria-label={`Tirar ${it.nome}`} onClick={() => onChange(itens.filter(x => x.id !== it.id))}
              style={{ background:"none", border:"none", color:C.danger, cursor:"pointer", fontSize:13, padding:0 }}>✕</button>
          </div>
        );
      })}
      {materiais.length ? (
        disponiveis.length > 0 && (
          <select aria-label="Adicionar caixa ou embalagem" value=""
            onChange={e => { const m = materiais.find(x => x.id === e.target.value); if (m) onChange([...itens, itemDeEmbalagem(m, 1)]); }}
            style={{ ...campo, color:C.muted }}>
            <option value="">+ Adicionar caixa / embalagem…</option>
            {disponiveis.map(m => <option key={m.id} value={m.id}>{m.nome} — {fmtMoeda(m.custo_unitario)}/{m.unidade || "un"}</option>)}
          </select>
        )
      ) : (
        <div style={dica}>Cadastre suas caixas e embalagens em Materiais (com o custo por unidade) pra escolher aqui — o estoque baixa sozinho.</div>
      )}
    </div>
  );
}

/* Maquininha/gateway, débito ou crédito Nx, % (editável) e quem paga a taxa.
   `valor` = { maquininha_id, cartao, cartao_pct, cartao_quem }. */
export function CartaoCampos({ valor, onChange, maquininhas, marketplace = false, calc = null, mostrarPct = true }) {
  const quem = valor.cartao_quem === "cliente" ? "cliente" : "loja";
  const temCartao = !!valor.maquininha_id || (mostrarPct && valor.cartao_pct !== "" && valor.cartao_pct != null);
  return (
    <div>
      <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
        <select aria-label="Maquininha ou gateway do cartão" value={valor.maquininha_id || ""}
          onChange={e => onChange({ maquininha_id:e.target.value, cartao:e.target.value ? (valor.cartao || "1") : "", cartao_pct:"" })}
          style={{ ...campo, flex:"1 1 160px", width:"auto" }}>
          <option value="">Sem cartão (Pix/dinheiro)</option>
          {maquininhas.map(m => <option key={m.id} value={m.id}>{m.nome || "Maquininha"}</option>)}
        </select>
        {valor.maquininha_id && (
          <select aria-label="Débito ou parcelas no crédito" value={valor.cartao || "1"}
            onChange={e => onChange({ cartao:e.target.value, cartao_pct:"" })}
            style={{ ...campo, flex:"0 1 130px", width:"auto" }}>
            <option value="debito">Débito</option>
            {Array.from({ length:MAX_PARCELAS }, (_, i) => String(i + 1)).map(n => <option key={n} value={n}>Crédito {n}x</option>)}
          </select>
        )}
        {mostrarPct && <>
          <input aria-label="Taxa do cartão em %" type="number" min="0" step="0.01" inputMode="decimal"
            value={valor.cartao_pct ?? ""} placeholder={calc ? calc.cartaoPct.toFixed(2) : "%"} onChange={e => onChange({ cartao_pct:e.target.value })}
            style={{ ...campo, flex:"0 1 80px", width:"auto" }} />
          <span style={{ color:C.muted, fontSize:12 }}>%</span>
        </>}
      </div>

      {!marketplace && temCartao && (
        <div style={{ marginTop:8 }}>
          <span style={{ ...rotulo, marginBottom:4 }}>QUEM PAGA A TAXA DO CARTÃO?</span>
          <div role="radiogroup" aria-label="Quem paga a taxa do cartão?" style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
            {[["loja", "🏪 A loja — sai do meu lucro"], ["cliente", "🧾 O cliente — cobrar a mais"]].map(([id, texto]) => (
              <button key={id} type="button" role="radio" aria-checked={quem === id} onClick={() => onChange({ cartao_quem:id })} style={chip(quem === id, C.accent2)}>{texto}</button>
            ))}
          </div>
        </div>
      )}

      {calc && calc.cartaoQuem === "cliente" && calc.acrescimoCartao > 0 && (
        <div style={{ marginTop:8, fontSize:12, color:C.text, background:withAlpha(C.accent2, "14"), border:"1px solid "+withAlpha(C.accent2, "44"), borderRadius:8, padding:"8px 10px", lineHeight:1.5 }}>
          💳 Cobre <strong style={{ color:C.accent2 }}>{fmtMoeda(calc.bruto)}</strong> no cartão (+{fmtMoeda(calc.acrescimoCartao)} de taxa) — assim a taxa sai do cliente e você recebe o valor cheio.
        </div>
      )}
      <div style={dica}>
        {marketplace
          ? "No marketplace a taxa do cartão normalmente já vem dentro da comissão, e não dá pra repassar pro comprador."
          : "A taxa vem das suas maquininhas (Configurações → Pagamentos); dá pra digitar o % do gateway."}
      </div>
    </div>
  );
}
